import {
    FetchState,
    ListParams
} from "@/shared/types";
import {
    UmbAccountUserList,
    UmbAccountUserModel,
    UmbAccountUserSearchState
} from "../types";
import * as storeHelper from "@/shared/storeHelper";
import {
    getUmbAccountUserById,
    getUmbAccountUsers
} from "@/shared/api/bankService";
import { AxiosError } from "axios";
import _ from "lodash";
import moment from "moment";
import Vue from "vue";
import { defineStore } from "pinia";
import { usePageSizeStore } from "@/shared/store/pageSize";

export const initialState: UmbAccountUserSearchState = {
    fetchState: FetchState.Uninitialized,
    pageNumber: 1,
    totalRecords: 0,
    users: [],
    userPages: []
};

export const useUmbAccountUserSearchStore = defineStore("umbAccountUserSearch", {
    state: (): UmbAccountUserSearchState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isLoading: state => state.fetchState === FetchState.Loading,
        pageCount () {
            const pageSizeStore = usePageSizeStore();
            const pageSize = pageSizeStore.pageSize;
            return this.totalRecords < pageSize
                ? 1
                : Math.floor((this.totalRecords + pageSize - 1) / pageSize);
        },
        pageSize () {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
        getUsersByPageNumber: state => (pageNumber: number): UmbAccountUserModel[] => {
            const pageObject = state.userPages[pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }

            return pageObject;
        },
        userDetail: state => (umbAccountUserId: number): UmbAccountUserModel => {
            return state.users.find(u => u.umbAccountUserId === umbAccountUserId);
        }
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setUsers (userList: UmbAccountUserList) {
            for (const user of userList.users) {
                if (user.dateOfBirth) {
                    user.dateOfBirth = moment(user.dateOfBirth).format("MM/DD/YYYY");
                }
            }

            const userPages = [ ...this.userPages ];
            userPages[userList.pageNumber] = userList.users;

            this.totalRecords = userList.totalRecords;
            this.pageNumber = userList.pageNumber;
            this.userPages = userPages;
            this.users = this.users.concat(userList.users);
        },
        clearUserPages () {
            this.pageNumber = 1;
            this.totalRecords = 0;
            this.users = [];
            this.userPages = [];
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setUser (user: UmbAccountUserModel) {
            const index = this.users.findIndex(u => u.umbAccountUserId === user.umbAccountUserId);

            if (index >= 0) {
                Vue.set(this.users, index, user);
            } else {
                this.users = [ user, ...this.users ];
            }
        },
        loadUser (umbAccountUserId: number) {
            const existingUser = _.find(this.users, u => u.umbAccountUserId === umbAccountUserId);

            if (existingUser) {
                return Promise.resolve(existingUser);
            }

            return getUmbAccountUserById(umbAccountUserId)
                .then(response => {
                    this.setUser(response.data);
                    return Promise.resolve(response.data);
                })
                .catch((error: AxiosError | any) => {
                    const displayError = error.response.status === 404 ? `User with id ${umbAccountUserId} not found`
                        : error;
                    storeHelper.handleError({ error: displayError, rethrow: false });
                    return Promise.reject();
                });
        },
        loadUsers ({ clearState, pageNumber, searchTerm }: ListParams) {
            if (this.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            if (clearState) {
                this.clearUserPages();
                this.setFetchState(FetchState.Loading);
                return getUmbAccountUsers(pageNumber, this.pageSize, searchTerm)
                    .then(response => {
                        this.setUsers(response.data);
                        this.setFetchState(FetchState.Loaded);
                    });
            }

            const pageObject = this.userPages[pageNumber || 1];
            if (typeof pageObject !== "undefined") {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);
            return getUmbAccountUsers(pageNumber, this.pageSize, searchTerm)
                .then(response => {
                    this.setUsers(response.data);
                    this.setFetchState(FetchState.Loaded);
                });
        },
        reset () {
            this.clearUserPages();
            this.setFetchState(FetchState.Uninitialized);
        }
    }
});
